import {
  AllDocumentationRequired,
  DocumentationRequired,
  DocumentationRequiredType,
  getAllDocumentationRequiredByLoanId,
  getLoanById,
  OnboardingDetailsType,
} from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';

import { usePageContext } from '@/contexts/PageContext/PageContext';
import { useCache } from '@/hooks/requests/useCache';

interface UseDocumentationProps {
  loanId: string;
}

export const useDocumentation = ({ loanId }: UseDocumentationProps) => {
  const {
    state: { applicationParticipant },
  } = usePageContext();
  const { data: loan } = useCache(getLoanById, { loanId });
  const { data: documentationRequired } = useCache(getAllDocumentationRequiredByLoanId, { loanId });

  const [allDocumentationRequired, setAllDocumentationRequired] = useState<AllDocumentationRequired | undefined>();
  const [selectedDocumentationRequired, setSelectedDocumentationRequired] = useState<
    DocumentationRequired | undefined
  >();

  useEffect(() => {
    if (applicationParticipant.onboardingDetails?.type === OnboardingDetailsType.BORROWER && allDocumentationRequired)
      setSelectedDocumentationRequired(
        allDocumentationRequired.borrowers.find(
          (borrower) =>
            applicationParticipant.onboardingDetails &&
            'borrowerId' in applicationParticipant.onboardingDetails &&
            borrower.borrowerId === applicationParticipant.onboardingDetails.borrowerId,
        ),
      );
  }, [applicationParticipant]);

  useEffect(() => {
    if (!documentationRequired || !loan) return;
    setAllDocumentationRequired(documentationRequired);

    if (selectedDocumentationRequired) {
      const borrowerDocumentationRequired = documentationRequired.borrowers.find(
        (documentationRequired) => documentationRequired.id === selectedDocumentationRequired.id,
      );
      if (borrowerDocumentationRequired) {
        setSelectedDocumentationRequired(borrowerDocumentationRequired);
        return;
      }
      if (documentationRequired.loan?.id === selectedDocumentationRequired.id) {
        setSelectedDocumentationRequired(documentationRequired.loan);
        return;
      }
    } else {
      if (documentationRequired.borrowers.length) {
        const borrowerDocumentationRequired = documentationRequired.borrowers.find(
          (documentationRequired) => documentationRequired.borrowerId === loan.borrowerId,
        );
        setSelectedDocumentationRequired(borrowerDocumentationRequired);
        return;
      } else {
        setSelectedDocumentationRequired(documentationRequired.loan ?? undefined);
        return;
      }
    }
  }, [documentationRequired, loan]);

  const selectedDocumentationRequiredType =
    selectedDocumentationRequired && 'borrowerId' in selectedDocumentationRequired
      ? DocumentationRequiredType.BORROWER
      : DocumentationRequiredType.LOAN;

  return {
    allDocumentationRequired,
    selectedDocumentationRequired,
    setSelectedDocumentationRequired,
    selectedDocumentationRequiredType,
    loan,
  };
};
